import { useState, useEffect } from 'react';
import {
  DollarSign,
  Cpu,
  Server,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  BarChart3,
  Zap,
  Database,
  Cloud,
  Edit,
  X,
  Plus,
  RefreshCw,
  Download,
} from 'lucide-react';
import type {
  CostSummary,
  ModelCost,
  InfraCostRecord,
  BudgetAlert,
  CostByProduct,
} from '../types/finops';
import './FinOps.css';

type TabType = 'overview' | 'ai' | 'infrastructure' | 'products' | 'alerts';

// Default empty state
const defaultSummary: CostSummary = {
  totalMonthly: 0,
  aiCosts: 0,
  infraCosts: 0,
  anthropicCosts: 0,
  openaiCosts: 0,
  vercelCosts: 0,
  railwayCosts: 0,
  supabaseCosts: 0,
  neonCosts: 0,
  budget: 0,
  budgetUsedPercent: 0,
  trend: 0,
};

export default function FinOps() {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [summary, setSummary] = useState<CostSummary>(defaultSummary);
  const [anthropicModels, setAnthropicModels] = useState<ModelCost[]>([]);
  const [openaiModels, setOpenaiModels] = useState<ModelCost[]>([]);
  const [infraCosts, setInfraCosts] = useState<InfraCostRecord[]>([]);
  const [productCosts, setProductCosts] = useState<CostByProduct[]>([]);
  const [alerts, setAlerts] = useState<BudgetAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [editBudget, setEditBudget] = useState<number>(0);
  const [savingBudget, setSavingBudget] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [savingCost, setSavingCost] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [newCost, setNewCost] = useState({
    category: 'ai' as 'ai' | 'infrastructure',
    provider: 'anthropic' as string,
    productName: '',
    usageType: '',
    cost: 0,
    usageQuantity: 0,
    usageUnit: '',
  });

  useEffect(() => {
    const fetchFinOpsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [summaryRes, aiCostsRes, infraRes, productRes, alertsRes] = await Promise.all([
          fetch('/api/finops/summary'),
          fetch('/api/finops/ai-costs'),
          fetch('/api/finops/infra-costs'),
          fetch('/api/finops/by-product'),
          fetch('/api/finops/alerts'),
        ]);

        if (summaryRes.ok) {
          const data = await summaryRes.json();
          setSummary(data);
        }
        if (aiCostsRes.ok) {
          const data = await aiCostsRes.json();
          setAnthropicModels(data.anthropic?.models || []);
          setOpenaiModels(data.openai?.models || []);
        }
        if (infraRes.ok) {
          const data = await infraRes.json();
          if (data?.costs) setInfraCosts(data.costs);
        }
        if (productRes.ok) {
          const data = await productRes.json();
          if (data?.products) setProductCosts(data.products);
        }
        if (alertsRes.ok) {
          const data = await alertsRes.json();
          if (data?.alerts) setAlerts(data.alerts);
        }
      } catch (err) {
        console.error('Failed to fetch FinOps data:', err);
        setError('Failed to load cost data');
      } finally {
        setLoading(false);
      }
    };

    fetchFinOpsData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  const getBudgetStatus = (percent: number) => {
    if (percent >= 90) return 'danger';
    if (percent >= 75) return 'warning';
    return 'safe';
  };

  const handleOpenBudgetModal = () => {
    setEditBudget(summary.budget);
    setShowBudgetModal(true);
  };

  const handleSaveBudget = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingBudget(true);
    try {
      // In production, this would POST to an API to update the budget
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSummary(prev => ({
        ...prev,
        budget: editBudget,
        budgetUsedPercent: editBudget > 0 ? (prev.totalMonthly / editBudget) * 100 : 0,
      }));
      setShowBudgetModal(false);
    } catch (err) {
      console.error('Failed to save budget:', err);
    } finally {
      setSavingBudget(false);
    }
  };

  const handleAddCost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newCost.cost <= 0) return;

    setSavingCost(true);
    try {
      const res = await fetch('/api/finops/costs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: newCost.category,
          provider: newCost.provider,
          productName: newCost.productName || undefined,
          usageType: newCost.usageType || 'manual_entry',
          cost: newCost.cost,
          usageQuantity: newCost.usageQuantity || undefined,
          usageUnit: newCost.usageUnit || undefined,
        }),
      });

      if (res.ok) {
        // Update local state
        const costAmount = newCost.cost;
        setSummary(prev => {
          const newTotal = prev.totalMonthly + costAmount;
          const isAi = newCost.category === 'ai';
          const newAi = isAi ? prev.aiCosts + costAmount : prev.aiCosts;
          const newInfra = !isAi ? prev.infraCosts + costAmount : prev.infraCosts;

          return {
            ...prev,
            totalMonthly: newTotal,
            aiCosts: newAi,
            infraCosts: newInfra,
            anthropicCosts: isAi && newCost.provider === 'anthropic' ? prev.anthropicCosts + costAmount : prev.anthropicCosts,
            openaiCosts: isAi && newCost.provider === 'openai' ? prev.openaiCosts + costAmount : prev.openaiCosts,
            vercelCosts: !isAi && newCost.provider === 'vercel' ? prev.vercelCosts + costAmount : prev.vercelCosts,
            railwayCosts: !isAi && newCost.provider === 'railway' ? prev.railwayCosts + costAmount : prev.railwayCosts,
            supabaseCosts: !isAi && newCost.provider === 'supabase' ? prev.supabaseCosts + costAmount : prev.supabaseCosts,
            neonCosts: !isAi && newCost.provider === 'neon' ? prev.neonCosts + costAmount : prev.neonCosts,
            budgetUsedPercent: prev.budget > 0 ? (newTotal / prev.budget) * 100 : 0,
          };
        });

        setShowCostModal(false);
        setNewCost({
          category: 'ai',
          provider: 'anthropic',
          productName: '',
          usageType: '',
          cost: 0,
          usageQuantity: 0,
          usageUnit: '',
        });
      } else {
        alert('Failed to add cost entry. Please try again.');
      }
    } catch (err) {
      console.error('Failed to add cost:', err);
      alert('Failed to add cost entry. Please try again.');
    } finally {
      setSavingCost(false);
    }
  };

  const handleSyncCosts = async () => {
    setSyncing(true);
    try {
      const res = await fetch('/api/sync/costs', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.costRecordsAdded > 0) {
          alert(`Successfully synced ${data.costRecordsAdded} cost records from provider APIs.`);
          refreshData();
        } else if (data.errors && data.errors.length > 0) {
          alert(`Sync completed with warnings:\n${data.errors.slice(0, 3).join('\n')}`);
        } else {
          const configuredProviders = Object.entries(data.providers)
            .filter(([, configured]) => configured)
            .map(([name]) => name);
          if (configuredProviders.length === 0) {
            alert('No provider API keys configured. Please add ANTHROPIC_ADMIN_API_KEY, OPENAI_ADMIN_API_KEY, VERCEL_TOKEN, RAILWAY_TOKEN, or SUPABASE_ACCESS_TOKEN to your environment.');
          } else {
            alert(`Sync completed. Configured providers: ${configuredProviders.join(', ')}`);
          }
        }
      } else {
        alert('Failed to sync costs. Check server logs for details.');
      }
    } catch (err) {
      console.error('Failed to sync costs:', err);
      alert('Failed to sync costs. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      const [summaryRes, aiCostsRes, infraRes, productRes, alertsRes] = await Promise.all([
        fetch('/api/finops/summary'),
        fetch('/api/finops/ai-costs'),
        fetch('/api/finops/infra-costs'),
        fetch('/api/finops/by-product'),
        fetch('/api/finops/alerts'),
      ]);

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
      if (aiCostsRes.ok) {
        const data = await aiCostsRes.json();
        setAnthropicModels(data.anthropic?.models || []);
        setOpenaiModels(data.openai?.models || []);
      }
      if (infraRes.ok) {
        const data = await infraRes.json();
        if (data?.costs) setInfraCosts(data.costs);
      }
      if (productRes.ok) {
        const data = await productRes.json();
        if (data?.products) setProductCosts(data.products);
      }
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        if (data?.alerts) setAlerts(data.alerts);
      }
    } catch (err) {
      console.error('Failed to refresh FinOps data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="finops-loading">Loading cost data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>FinOps</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f38ba8' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <h1>FinOps</h1>
          <p>AI and infrastructure cost tracking</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={handleSyncCosts} disabled={syncing}>
            <Download size={16} className={syncing ? 'spin' : ''} />
            {syncing ? 'Syncing...' : 'Sync from APIs'}
          </button>
          <button className="btn btn-secondary" onClick={refreshData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowCostModal(true)}>
            <Plus size={16} />
            Add Cost
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="finops-stats">
        <div className="finops-stat">
          <div className="finops-stat-icon total">
            <DollarSign size={24} />
          </div>
          <div className="finops-stat-content">
            <div className="finops-stat-value">{formatCurrency(summary.totalMonthly)}</div>
            <div className="finops-stat-label">Total This Month</div>
            <span className={`finops-stat-trend ${summary.trend > 0 ? 'up' : 'down'}`}>
              {summary.trend > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {Math.abs(summary.trend)}% vs last month
            </span>
          </div>
        </div>
        <div className="finops-stat">
          <div className="finops-stat-icon ai">
            <Cpu size={24} />
          </div>
          <div className="finops-stat-content">
            <div className="finops-stat-value">{formatCurrency(summary.aiCosts)}</div>
            <div className="finops-stat-label">AI Costs</div>
          </div>
        </div>
        <div className="finops-stat">
          <div className="finops-stat-icon infra">
            <Server size={24} />
          </div>
          <div className="finops-stat-content">
            <div className="finops-stat-value">{formatCurrency(summary.infraCosts)}</div>
            <div className="finops-stat-label">Infrastructure</div>
          </div>
        </div>
        <div className="finops-stat">
          <div className="finops-stat-icon budget">
            <BarChart3 size={24} />
          </div>
          <div className="finops-stat-content">
            <div className="finops-stat-value">{summary.budgetUsedPercent.toFixed(1)}%</div>
            <div className="finops-stat-label">Budget Used</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="finops-tabs">
        <button
          className={`finops-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={16} />
          Overview
        </button>
        <button
          className={`finops-tab ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          <Cpu size={16} />
          AI Costs
        </button>
        <button
          className={`finops-tab ${activeTab === 'infrastructure' ? 'active' : ''}`}
          onClick={() => setActiveTab('infrastructure')}
        >
          <Server size={16} />
          Infrastructure
        </button>
        <button
          className={`finops-tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          <Zap size={16} />
          By Product
        </button>
        <button
          className={`finops-tab ${activeTab === 'alerts' ? 'active' : ''}`}
          onClick={() => setActiveTab('alerts')}
        >
          <AlertTriangle size={16} />
          Alerts ({alerts.length})
        </button>
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <>
          <div className="cost-grid">
            <div className="cost-card">
              <div className="cost-card-header">
                <div className="cost-card-title">
                  <Cpu size={18} />
                  AI Providers
                </div>
                <div className="cost-card-total">{formatCurrency(summary.aiCosts)}</div>
              </div>
              <div className="provider-list">
                <div className="provider-item">
                  <div className="provider-info">
                    <div className="provider-icon anthropic">A</div>
                    <div>
                      <div className="provider-name">Anthropic</div>
                      <div className="provider-details">Claude models</div>
                    </div>
                  </div>
                  <div className="provider-cost">{formatCurrency(summary.anthropicCosts)}</div>
                </div>
                <div className="provider-item">
                  <div className="provider-info">
                    <div className="provider-icon openai">O</div>
                    <div>
                      <div className="provider-name">OpenAI</div>
                      <div className="provider-details">GPT + Embeddings</div>
                    </div>
                  </div>
                  <div className="provider-cost">{formatCurrency(summary.openaiCosts)}</div>
                </div>
              </div>
            </div>

            <div className="cost-card">
              <div className="cost-card-header">
                <div className="cost-card-title">
                  <Server size={18} />
                  Infrastructure
                </div>
                <div className="cost-card-total">{formatCurrency(summary.infraCosts)}</div>
              </div>
              <div className="provider-list">
                <div className="provider-item">
                  <div className="provider-info">
                    <div className="provider-icon supabase">S</div>
                    <div>
                      <div className="provider-name">Supabase</div>
                      <div className="provider-details">Database + Auth</div>
                    </div>
                  </div>
                  <div className="provider-cost">{formatCurrency(summary.supabaseCosts)}</div>
                </div>
                <div className="provider-item">
                  <div className="provider-info">
                    <div className="provider-icon railway">R</div>
                    <div>
                      <div className="provider-name">Railway</div>
                      <div className="provider-details">Backend Services</div>
                    </div>
                  </div>
                  <div className="provider-cost">{formatCurrency(summary.railwayCosts)}</div>
                </div>
                <div className="provider-item">
                  <div className="provider-info">
                    <div className="provider-icon neon">N</div>
                    <div>
                      <div className="provider-name">Neon</div>
                      <div className="provider-details">PostgreSQL</div>
                    </div>
                  </div>
                  <div className="provider-cost">{formatCurrency(summary.neonCosts)}</div>
                </div>
                <div className="provider-item">
                  <div className="provider-info">
                    <div className="provider-icon vercel">V</div>
                    <div>
                      <div className="provider-name">Vercel</div>
                      <div className="provider-details">Frontend Hosting</div>
                    </div>
                  </div>
                  <div className="provider-cost">{formatCurrency(summary.vercelCosts)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="cost-card">
            <div className="cost-card-header">
              <div className="cost-card-title">Monthly Budget</div>
              <div className="cost-card-total" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {formatCurrency(summary.budget)}
                <button className="btn btn-icon" onClick={handleOpenBudgetModal} title="Edit Budget">
                  <Edit size={16} />
                </button>
              </div>
            </div>
            <div className="budget-progress">
              <div className="budget-bar">
                <div
                  className={`budget-bar-fill ${getBudgetStatus(summary.budgetUsedPercent)}`}
                  style={{ width: `${Math.min(summary.budgetUsedPercent, 100)}%` }}
                />
              </div>
              <div className="budget-labels">
                <span>{formatCurrency(summary.totalMonthly)} used</span>
                <span>{formatCurrency(summary.budget - summary.totalMonthly)} remaining</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* AI Costs */}
      {activeTab === 'ai' && (
        <div className="cost-grid">
          <div className="cost-card">
            <div className="cost-card-header">
              <div className="cost-card-title">
                <span className="provider-icon anthropic" style={{ width: '24px', height: '24px', fontSize: '0.7rem' }}>A</span>
                Anthropic
              </div>
              <div className="cost-card-total">{formatCurrency(summary.anthropicCosts)}</div>
            </div>
            <div className="model-list">
              {anthropicModels.map((model) => (
                <div key={model.model} className="model-item">
                  <div>
                    <div className="model-name">{model.model}</div>
                    <div className="model-stats">
                      <span>{formatNumber(model.requests)} requests</span>
                      <span>{formatNumber(model.inputTokens)} in / {formatNumber(model.outputTokens)} out</span>
                    </div>
                  </div>
                  <div className="model-cost">{formatCurrency(model.cost)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="cost-card">
            <div className="cost-card-header">
              <div className="cost-card-title">
                <span className="provider-icon openai" style={{ width: '24px', height: '24px', fontSize: '0.7rem' }}>O</span>
                OpenAI
              </div>
              <div className="cost-card-total">{formatCurrency(summary.openaiCosts)}</div>
            </div>
            <div className="model-list">
              {openaiModels.map((model) => (
                <div key={model.model} className="model-item">
                  <div>
                    <div className="model-name">{model.model}</div>
                    <div className="model-stats">
                      <span>{formatNumber(model.requests)} requests</span>
                      <span>{formatNumber(model.inputTokens)} in / {formatNumber(model.outputTokens)} out</span>
                    </div>
                  </div>
                  <div className="model-cost">{formatCurrency(model.cost)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Infrastructure */}
      {activeTab === 'infrastructure' && (
        <div className="cost-card">
          <div className="cost-card-header">
            <div className="cost-card-title">Infrastructure Costs</div>
            <div className="cost-card-total">{formatCurrency(summary.infraCosts)}</div>
          </div>
          <div className="provider-list">
            {infraCosts.map((cost) => (
              <div key={cost.id} className="provider-item">
                <div className="provider-info">
                  <div className={`provider-icon ${cost.provider}`}>
                    {cost.provider === 'vercel' && 'V'}
                    {cost.provider === 'railway' && 'R'}
                    {cost.provider === 'supabase' && 'S'}
                    {cost.provider === 'neon' && 'N'}
                  </div>
                  <div>
                    <div className="provider-name">{cost.productName || cost.provider}</div>
                    <div className="provider-details">
                      {cost.usageType}{cost.usageQuantity ? `: ${cost.usageQuantity} ${cost.usageUnit}` : ''}
                    </div>
                  </div>
                </div>
                <div className="provider-cost">{formatCurrency(cost.cost)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By Product */}
      {activeTab === 'products' && (
        <div className="cost-card">
          <div className="cost-card-header">
            <div className="cost-card-title">Cost by Product</div>
          </div>
          <table className="product-costs-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>AI Cost</th>
                <th>Infra Cost</th>
                <th>Total</th>
                <th>Budget</th>
                <th>Usage</th>
              </tr>
            </thead>
            <tbody>
              {productCosts.map((product) => {
                const usagePercent = product.budget ? (product.totalCost / product.budget) * 100 : 0;
                return (
                  <tr key={product.productId}>
                    <td className="product-name">{product.productName}</td>
                    <td className="cost-cell">{formatCurrency(product.aiCost)}</td>
                    <td className="cost-cell">{formatCurrency(product.infraCost)}</td>
                    <td className="cost-cell" style={{ fontWeight: 600 }}>{formatCurrency(product.totalCost)}</td>
                    <td className="cost-cell">{product.budget ? formatCurrency(product.budget) : '-'}</td>
                    <td>
                      {product.budget && (
                        <span className={`severity-badge ${getBudgetStatus(usagePercent)}`} style={{ textTransform: 'none' }}>
                          {usagePercent.toFixed(0)}%
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Alerts */}
      {activeTab === 'alerts' && (
        <div className="alerts-list">
          {alerts.length === 0 ? (
            <div className="finops-empty">
              <div className="finops-empty-icon">
                <DollarSign size={48} />
              </div>
              <h3>No budget alerts</h3>
              <p>All costs are within budget.</p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div key={alert.id} className={`alert-item ${alert.severity}`}>
                <div className={`alert-icon ${alert.severity}`}>
                  <AlertTriangle size={18} />
                </div>
                <div className="alert-content">
                  <div className="alert-title">
                    {alert.alertType === 'threshold' && 'Budget Threshold Alert'}
                    {alert.alertType === 'forecast' && 'Budget Forecast Alert'}
                    {alert.alertType === 'anomaly' && 'Cost Anomaly Detected'}
                  </div>
                  <div className="alert-message">{alert.message}</div>
                  <div className="alert-time">{formatTimeAgo(alert.createdAt)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Budget Edit Modal */}
      {showBudgetModal && (
        <div className="modal-overlay" onClick={() => setShowBudgetModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Monthly Budget</h2>
              <button className="close-btn" onClick={() => setShowBudgetModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSaveBudget}>
              <div className="form-group">
                <label>Monthly Budget Amount (USD)</label>
                <div className="budget-input-wrapper">
                  <DollarSign size={18} className="budget-input-icon" />
                  <input
                    type="number"
                    value={editBudget}
                    onChange={(e) => setEditBudget(parseFloat(e.target.value) || 0)}
                    min="0"
                    step="100"
                    className="budget-input"
                    required
                  />
                </div>
                <p className="form-hint">
                  Current spend: {formatCurrency(summary.totalMonthly)} ({editBudget > 0 ? ((summary.totalMonthly / editBudget) * 100).toFixed(1) : 0}% of budget)
                </p>
              </div>

              <div className="budget-thresholds">
                <h4>Alert Thresholds</h4>
                <div className="threshold-item">
                  <span className="threshold-badge warning">Warning</span>
                  <span>75% of budget ({formatCurrency(editBudget * 0.75)})</span>
                </div>
                <div className="threshold-item">
                  <span className="threshold-badge danger">Critical</span>
                  <span>90% of budget ({formatCurrency(editBudget * 0.90)})</span>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBudgetModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={savingBudget}>
                  {savingBudget ? 'Saving...' : 'Save Budget'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Cost Modal */}
      {showCostModal && (
        <div className="modal-overlay" onClick={() => setShowCostModal(false)}>
          <div className="modal add-cost-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Cost Entry</h2>
              <button className="close-btn" onClick={() => setShowCostModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddCost}>
              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={newCost.category}
                    onChange={(e) => setNewCost({
                      ...newCost,
                      category: e.target.value as 'ai' | 'infrastructure',
                      provider: e.target.value === 'ai' ? 'anthropic' : 'vercel',
                    })}
                  >
                    <option value="ai">AI Provider</option>
                    <option value="infrastructure">Infrastructure</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Provider *</label>
                  <select
                    value={newCost.provider}
                    onChange={(e) => setNewCost({ ...newCost, provider: e.target.value })}
                  >
                    {newCost.category === 'ai' ? (
                      <>
                        <option value="anthropic">Anthropic</option>
                        <option value="openai">OpenAI</option>
                      </>
                    ) : (
                      <>
                        <option value="vercel">Vercel</option>
                        <option value="railway">Railway</option>
                        <option value="supabase">Supabase</option>
                        <option value="neon">Neon</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Product/Service Name</label>
                <input
                  type="text"
                  value={newCost.productName}
                  onChange={(e) => setNewCost({ ...newCost, productName: e.target.value })}
                  placeholder="e.g., Resolvr Gateway, Claude API"
                />
              </div>

              <div className="form-row">
                <div className="form-group flex-2">
                  <label>Cost Amount (USD) *</label>
                  <div className="budget-input-wrapper">
                    <DollarSign size={18} className="budget-input-icon" />
                    <input
                      type="number"
                      value={newCost.cost || ''}
                      onChange={(e) => setNewCost({ ...newCost, cost: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="budget-input"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Usage Type</label>
                  <input
                    type="text"
                    value={newCost.usageType}
                    onChange={(e) => setNewCost({ ...newCost, usageType: e.target.value })}
                    placeholder="e.g., compute, tokens, storage"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Usage Quantity</label>
                  <input
                    type="number"
                    value={newCost.usageQuantity || ''}
                    onChange={(e) => setNewCost({ ...newCost, usageQuantity: parseFloat(e.target.value) || 0 })}
                    min="0"
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Usage Unit</label>
                  <input
                    type="text"
                    value={newCost.usageUnit}
                    onChange={(e) => setNewCost({ ...newCost, usageUnit: e.target.value })}
                    placeholder="e.g., tokens, GB, hours"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCostModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={savingCost || newCost.cost <= 0}>
                  <Plus size={16} />
                  {savingCost ? 'Adding...' : 'Add Cost'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
